// Cache d'offres pour le prerender (SSR statique au build).
//
// Problème résolu : les pages d'offres (/bnssa/offres-emploi/:jobSlug et
// /maitre-nageur-sauveteur/offres-emploi/:jobSlug) chargent le `job` de façon
// ASYNCHRONE (useEffect + recruiter_jobs.list). Or `renderToString` (utilisé par
// vite-prerender-plugin) est SYNCHRONE et n'attend pas les effects. Résultat :
// au moment du prerender, le `job` est `null`, donc <Seo> n'écrit pas le bon
// <title>/og:title, et le HTML brut servi aux crawlers (LinkedIn/Facebook)
// retombe sur le <title> générique d'index.html.
//
// Solution : au build, prerender.tsx lit recruiter_jobs UNE fois et peuple ce
// cache mémoire. JobDetail lit ce cache de façon SYNCHRONE pour initialiser le
// `job` dès le premier rendu serveur, ce qui permet à <Seo> d'émettre le bon
// title/OG dans le HTML statique de chaque offre.
//
// Côté client (navigateur), ce cache reste vide et JobDetail conserve son
// chargement asynchrone habituel : aucun impact sur l'UX runtime.

import type { Job } from "@/data/jobs";

// Map id (string) -> Job résolu, peuplée uniquement pendant le prerender.
const jobCache = new Map<string, Job>();

/**
 * Enregistre une offre dans le cache de prerender (appelé au build).
 */
export function registerPrerenderJob(job: Job): void {
  if (!job?.id) return;
  jobCache.set(String(job.id), job);
}

/**
 * Enregistre plusieurs offres d'un coup (appelé au build).
 */
export function registerPrerenderJobs(list: Job[]): void {
  for (const job of list) registerPrerenderJob(job);
}

/**
 * Récupère SYNCHRONIQUEMENT une offre du cache de prerender par son id.
 * Renvoie null côté client (cache vide) ou si l'id est inconnu.
 */
export function getPrerenderJob(id: string | null | undefined): Job | null {
  if (!id) return null;
  return jobCache.get(String(id)) ?? null;
}

/**
 * Renvoie toutes les offres mises en cache (utilisé pour générer les links).
 */
export function getAllPrerenderJobs(): Job[] {
  return Array.from(jobCache.values());
}
